import React from 'react'
import { SelectChangeEvent } from '@mui/material'
import { useIntl } from 'react-intl'
import { LoanLog } from '@matech/thebigpos-sdk'
import { Select } from '../formControls'

export type LoanLogType = LoanLog['type']
export type LogLevel = LoanLog['level']

type LoanLogSearchFiltersProps = {
	handleFilterChange: (
		e: SelectChangeEvent<string | string[]>,
		filterName: string
	) => void
	searchCriteria: {
		types?: LoanLogType[] | LoanLogType | 'all'
		levels?: LogLevel[] | LogLevel | 'all'
	}
}

const LoanLogSearchFilters = ({
	handleFilterChange,
	searchCriteria,
}: LoanLogSearchFiltersProps) => {
	const { formatMessage } = useIntl()

	// Map for custom type display names
	const typeDisplayNames: Record<string, string> = {
		POSFlagChanged: formatMessage({
			id: 'loan_log.type.pos_flag_changed',
			defaultMessage: 'POS Flag Changed',
		}),
	}

	// Generate type display labels based on the actual values
	const getTypeDisplayName = (type: string): string => {
		return typeDisplayNames[type] ?? type
	}

	// Generate level display labels based on the actual values
	const getLevelDisplayName = (level: string): string => {
		return level
	}

	// Available types and levels - these should match what the API returns
	const availableTypes: LoanLogType[] = [
		'Loan',
		'Queue',
		'POSFlagChanged',
		'Verification',
	]

	const availableLevels: LogLevel[] = [
		'None',
		'Info',
		'Warning',
		'Error',
	]

	// Get array value for multi-select
	const getArrayValue = (
		value:
			| LoanLogType[]
			| LoanLogType
			| LogLevel[]
			| LogLevel
			| 'all'
			| undefined
	): string[] => {
		if (!value || value === 'all') {
			return []
		}
		if (Array.isArray(value)) {
			return value
		}
		return [value]
	}

	return (
		<>
			<Select
				name="type"
				label={formatMessage({ id: 'global.type' })}
				displayEmpty
				menuItems={availableTypes.map((type) => ({
					value: type,
					label: getTypeDisplayName(type),
				}))}
				selectProps={{
					multiple: true,
					value: getArrayValue(searchCriteria.types),
					onChange: (e) =>
						handleFilterChange(
							e as SelectChangeEvent<string | string[]>,
							'types'
						),
				}}
				controlled={false}
			/>
			<Select
				name="level"
				label={formatMessage({ id: 'global.level' })}
				displayEmpty
				menuItems={availableLevels.map((level) => ({
					value: level,
					label: getLevelDisplayName(level),
				}))}
				selectProps={{
					multiple: true,
					value: getArrayValue(searchCriteria.levels),
					onChange: (e) =>
						handleFilterChange(
							e as SelectChangeEvent<string | string[]>,
							'levels'
						),
				}}
				controlled={false}
			/>
		</>
	)
}

export default LoanLogSearchFilters
